/**
 * O que os DOIS lados da proposta precisam saber sobre um item: o documento que
 * o cliente lê e o modal onde o vendedor monta. Mora aqui, e não dentro de um
 * deles, porque critério duplicado vira duas verdades — e aí uma linha sai
 * "Incluso" para o cliente e "R$ 0,00" para quem montou.
 */

/**
 * A LINHA É DO QUE JÁ VEM NO PACOTE?
 *
 * O cadastro acompanha todo pacote de uma linha do produto-membro com a
 * quantidade inclusa e valor zero — em PRO20260086, o HUB-START-VOZ vem com uma
 * linha HUB-RAMAL de 10 unidades a zero. É essa linha que faz a capa dizer
 * "10 ramais", porque o `resumoDeVoz` soma quantidade e ignora preço.
 *
 * O efeito colateral é na composição: a linha imprimia "R$ 0,00" no unitário e
 * no subtotal. Item a zero numa proposta lê como brinde ou como erro de sistema,
 * e nenhum dos dois é o que está acontecendo — aquilo já está pago dentro do
 * pacote da linha de cima.
 *
 * Só vale para unitário zero. Item com 100% de desconto também fecha em zero,
 * mas ali o subtotal zero é consequência do desconto, que a coluna ao lado já
 * declara — trocar por "Incluso" esconderia o desconto.
 */
export function linhaInclusa(item: any): boolean {
  return Number(item?.unitPrice) === 0 && Number(item?.subtotal) === 0 && Number(item?.quantity) > 0;
}

/**
 * MOVER UM ITEM DE LUGAR NA COMPOSIÇÃO.
 *
 * A ordem do array é a ordem impressa no documento: ordem aqui é CONTEÚDO, não
 * estética. Até agora só se corrigia por SQL — a PRO20260086 foi reordenada por
 * comando no banco três vezes num único dia, porque item adicionado fora de
 * lugar deixava a composição embaralhada e o vendedor não tinha como consertar.
 *
 * `para` é a posição que o item vai OCUPAR, lida na lista original: soltar o
 * item 0 sobre o item 2 devolve [1, 2, 0, 3]. Tirar-e-inserir com splice acerta
 * os dois sentidos, e é justamente a conta que erra sozinha quando escrita na
 * mão dentro do componente — ao mover para baixo, o índice de destino anda
 * depois da remoção.
 *
 * Devolve a MESMA lista quando nada muda, para não disparar render à toa.
 */
export function moverItem<T>(lista: T[], de: number, para: number): T[] {
  if (!Array.isArray(lista)) return lista;
  const ultimo = lista.length - 1;
  if (de < 0 || de > ultimo || para < 0 || para > ultimo || de === para) return lista;
  const copia = [...lista];
  const [item] = copia.splice(de, 1);
  copia.splice(para, 0, item);
  return copia;
}

/**
 * QUANTIDADE QUE MULTIPLICA O UNITÁRIO.
 *
 * Desde a regra nova de faixas (backend #231), a faixa vale para TODAS as
 * unidades e o total de um produto escalonado é
 *
 *     total(q) = min sobre as faixas f de ( max(q, f.minQuantity) × f.unitPrice )
 *
 * o que faz quem tem 190 ser cobrado como 191, porque sai mais barato. A
 * consequência para a tela é que a **quantidade faturada pode ser maior que a
 * contratada**, e aí `quantidade × unitário` DEIXA de dar o subtotal.
 *
 * Quem calcula é o backend (`pricingService.quote` devolve `billedQuantity`);
 * aqui só se lê o que ele disse. Esta função existe para que os dois lugares
 * que recompõem subtotal na tela — mudar quantidade e mudar desconto — não
 * voltem a multiplicar pela quantidade contratada e mostrar menos do que a
 * fatura vai cobrar.
 *
 * ⛔ Preço NEGOCIADO é exceção: é por unidade CONTRATADA. Quem digita o
 * unitário à mão sai do Guia de Preços, e por isso quem edita o unitário
 * precisa LIMPAR o `billedQuantity` — senão o negociado seria cobrado sobre um
 * mínimo de faixa que o cliente não alcançou, que é o oposto de conceder
 * desconto.
 */
export function quantidadeFaturada(item: any): number {
  const contratada = Number(item?.quantity) || 0;
  const faturada = Number(item?.billedQuantity) || 0;
  return faturada > contratada ? faturada : contratada;
}

/** `true` quando a fatura conta mais unidades do que o cliente contratou. */
export function cobraAcimaDoContratado(item: any): boolean {
  return quantidadeFaturada(item) > (Number(item?.quantity) || 0);
}

/**
 * Subtotal da linha a partir do que está na tela. O desconto negociado incide
 * sobre o resultado, como já incidia.
 */
export function subtotalDaLinha(item: any): number {
  const unit = Number(item?.unitPrice) || 0;
  const desconto = Number(item?.discount) || 0;
  return unit * quantidadeFaturada(item) * (1 - desconto / 100);
}
